// helpers/mealDistribution.ts
// Unified meal distribution utilities used by quiz results and account modals

/**
 * Recipe priority order for auto-distribution.
 * Higher priority recipes (lower index) get allocated first when distributing
 * packs — meat recipes (lamb/beef/turkey) rank above the salmon recipes, so
 * leftover packs skew toward meat.
 */
export const RECIPE_PRIORITY = ["Lamb", "Beef", "Turkey", "Salmon", "Salmon & Turkey"];

/**
 * Get the priority index for a recipe based on its title.
 * Lower index = higher priority. When several entries match a title, the most
 * specific (longest) match wins — "Sammy's salmon & turkey sensation" ranks at
 * "Salmon & Turkey", not at "Turkey" or "Salmon".
 */
export function getRecipePriorityIndex(title: string): number {
  const lowerTitle = title.toLowerCase();
  let bestIndex = RECIPE_PRIORITY.length + 1; // unknown recipes get lowest priority
  let bestSpecificity = 0;
  for (let i = 0; i < RECIPE_PRIORITY.length; i++) {
    const name = RECIPE_PRIORITY[i].toLowerCase();
    if (lowerTitle.includes(name) && name.length > bestSpecificity) {
      bestIndex = i;
      bestSpecificity = name.length;
    }
  }
  return bestIndex;
}

/**
 * Sort recipes by priority (highest priority first).
 */
export function sortRecipesByPriority<T extends { title: string }>(recipes: T[]): T[] {
  return [...recipes].sort(
    (a, b) => getRecipePriorityIndex(a.title) - getRecipePriorityIndex(b.title)
  );
}

/**
 * Extra title keywords implied by an allergy — recipe titles name proteins,
 * not allergen classes (a "Fish" allergy must catch the Salmon recipes).
 */
const ALLERGY_TITLE_SYNONYMS: Record<string, string[]> = {
  fish: ["salmon"],
};

/**
 * Products whose titles don't name everything they contain. Keys are matched
 * case-insensitively as title substrings; values are the allergens the
 * product contains beyond what its title says (e.g. Sammy's & Tilly's is
 * chicken-based but doesn't say so in the title).
 */
const HIDDEN_ALLERGENS_BY_TITLE: Record<string, string[]> = {
  "sammy": ["chicken"],
  "tilly": ["chicken"],
};

/**
 * Filter recipes by allergies.
 * Removes any recipe whose title contains an allergy keyword, an allergy
 * synonym (fish → salmon), or is known to contain the allergen despite its
 * title (see HIDDEN_ALLERGENS_BY_TITLE).
 */
export function filterRecipesByAllergies<T extends { title: string }>(
  recipes: T[],
  allergies: string[]
): T[] {
  if (!allergies?.length) return recipes;
  const active = allergies.map((a) => a.toLowerCase());

  return recipes.filter((recipe) => {
    const lowerTitle = recipe.title.toLowerCase();
    const hidden = Object.entries(HIDDEN_ALLERGENS_BY_TITLE)
      .filter(([key]) => lowerTitle.includes(key))
      .flatMap(([, allergens]) => allergens);

    return !active.some((allergy) => {
      if (lowerTitle.includes(allergy)) return true;
      if (hidden.includes(allergy)) return true;
      return (ALLERGY_TITLE_SYNONYMS[allergy] ?? []).some((syn) => lowerTitle.includes(syn));
    });
  });
}

/**
 * Recipes matching these title keywords keep their even baseline but are
 * excluded from leftover packs — we promote them less than the meat recipes.
 */
const DEPRIORITIZED_KEYWORDS = ["salmon"];

function isDeprioritized(title: string): boolean {
  const t = title.toLowerCase();
  return DEPRIORITIZED_KEYWORDS.some((k) => t.includes(k));
}

/**
 * Auto-distribute packs evenly across recipes, with priority-based allocation.
 *
 * Algorithm:
 * 1. Each recipe gets at least 1 pack (or base = floor(total / count))
 * 2. Remaining packs are distributed round-robin starting from highest
 *    priority — skipping deprioritized recipes (salmon), which never receive
 *    leftovers.
 * 3. Each deprioritized recipe then gives up one pack (never dropping below 1)
 *    to the meat recipes, so salmon lands BELOW the even share, not at it.
 * If only deprioritized recipes survive (e.g. after allergy filtering), they
 * are treated normally.
 *
 * @param recipes - Array of recipes (must have variantId property)
 * @param totalPacks - Total number of packs to distribute
 * @returns Record mapping variantId to count
 */
export function autoDistributePacks<T extends { variantId: string; title?: string }>(
  recipes: T[],
  totalPacks: number
): Record<string, number> {
  if (!recipes.length || totalPacks <= 0) return {};

  const result: Record<string, number> = {};
  let remaining = totalPacks;

  // Step 1: Calculate even baseline (at least 1 per recipe)
  const base = Math.floor(totalPacks / recipes.length);

  for (const recipe of recipes) {
    const take = Math.max(base, 1);
    result[recipe.variantId] = take;
    remaining -= take;
    if (remaining <= 0) break;
  }

  // Step 2: Distribute leftovers round-robin (priority order preserved),
  // capping deprioritized recipes at their baseline when possible
  const preferred = recipes.filter((r) => !isDeprioritized(String(r.title ?? "")));
  const deprioritized = recipes.filter((r) => isDeprioritized(String(r.title ?? "")));
  const leftoverPool = preferred.length > 0 ? preferred : recipes;

  while (remaining > 0) {
    for (const recipe of leftoverPool) {
      if (remaining <= 0) break;
      result[recipe.variantId] = (result[recipe.variantId] ?? 0) + 1;
      remaining--;
    }
  }

  // Step 3: Move one pack from each deprioritized recipe to the meat recipes,
  // so salmon sits below the even share (but always keeps at least 1)
  if (preferred.length > 0) {
    let transfers = 0;
    for (const recipe of deprioritized) {
      if ((result[recipe.variantId] ?? 0) > 1) {
        result[recipe.variantId] -= 1;
        transfers++;
      }
    }
    while (transfers > 0) {
      for (const recipe of preferred) {
        if (transfers <= 0) break;
        result[recipe.variantId] = (result[recipe.variantId] ?? 0) + 1;
        transfers--;
      }
    }
  }

  return result;
}

/**
 * Meal selection item with baseline tracking for account modals.
 * The baseline is the auto-distributed amount that users cannot go below.
 */
export type MealSelectionWithBaseline = {
  variantId: string;
  count: number;
  baseline: number;
  productTitle: string;
  variantTitle: string;
  imageUrl: string | null;
  price: number;
};

/**
 * Build meal selections with baseline from products and distribution.
 * Used by account modals where users can add but not remove below baseline.
 *
 * @param products - Shopify products (meals)
 * @param totalPacks - Total packs to distribute
 * @param packSize - Pack size in grams to filter variants
 * @param findVariantForPackSize - Function to find the correct variant for a pack size
 * @param allergies - Optional allergies to filter out
 */
export function buildMealSelectionsWithBaseline<
  TProduct extends { id: string | number; title: string; image?: { src?: string } | null },
  TVariant extends { id: string | number; title: string; price: string | number }
>(
  products: TProduct[],
  totalPacks: number,
  packSize: number,
  findVariantForPackSize: (product: TProduct, packSize: number) => TVariant | null,
  allergies: string[] = []
): MealSelectionWithBaseline[] {
  // Build recipe list with variants
  const recipes: Array<{
    variantId: string;
    productTitle: string;
    variantTitle: string;
    imageUrl: string | null;
    price: number;
    title: string; // for filtering/sorting
  }> = [];

  for (const product of products) {
    const variant = findVariantForPackSize(product, packSize);
    if (!variant) continue;

    recipes.push({
      variantId: String(variant.id),
      productTitle: product.title,
      variantTitle: variant.title,
      imageUrl: product.image?.src || null,
      price: typeof variant.price === "string" ? parseFloat(variant.price) : variant.price,
      title: product.title, // used for filtering/sorting
    });
  }

  // Filter by allergies
  const filtered = filterRecipesByAllergies(recipes, allergies);

  // Sort by priority
  const sorted = sortRecipesByPriority(filtered);

  // Auto-distribute
  const distribution = autoDistributePacks(sorted, totalPacks);

  // Build final selections with baseline
  return sorted.map((recipe) => ({
    variantId: recipe.variantId,
    count: distribution[recipe.variantId] || 0,
    baseline: distribution[recipe.variantId] || 0, // baseline = initial distribution
    productTitle: recipe.productTitle,
    variantTitle: recipe.variantTitle,
    imageUrl: recipe.imageUrl,
    price: recipe.price,
  }));
}

/**
 * Simple meal selection item (for API calls).
 */
export type MealSelectionItem = {
  variantId: string;
  count: number;
};

/**
 * Convert meal selections to API format (strips extra fields).
 */
export function toMealSelectionItems(
  selections: Array<{ variantId: string; count: number }>
): MealSelectionItem[] {
  return selections
    .filter((s) => s.count > 0)
    .map((s) => ({ variantId: s.variantId, count: s.count }));
}
